// Casca comum das páginas /master/* (exceto o login) — exige sessão de
// PlatformAdmin válida (redireciona para /master/login sem uma) e mostra
// uma barra fixa simples com o nome do admin logado e "Sair". Sem sidebar
// nem mapa: este painel não tem nada em comum com o app de empresa (ver
// __root.tsx#isPlatformRoute), é só uma casca própria e enxuta. Nav de
// abas adicionada 2026-09-04 (Notificações) — antes só existia "Empresas",
// então não havia nada pra navegar entre.
import { useEffect, useState, type ReactNode } from "react";
import { GpsBootLoader } from "@/components/gps-loader";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Bell, Box, Building2, KeyRound, Link2, LogOut, MessageSquare, Plug, Radio, Server, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlatformAuth } from "@/contexts/platform-auth-context";
import { MasterChangePasswordDialog } from "@/components/master-change-password-dialog";
import { SMS_TABS } from "@/components/master-sms-tabs";

const NAV_ITEMS = [
  { to: "/master", label: "Empresas", icon: Building2 },
  { to: "/master/notifications", label: "Notificações", icon: Bell },
  { to: "/master/app", label: "Aplicativo", icon: Smartphone },
  { to: "/master/icon-models", label: "Modelos 3D", icon: Box },
  { to: "/master/vehicle-link", label: "Vincular veículos", icon: Link2 },
  { to: "/master/sms", label: "SMS", icon: MessageSquare },
  { to: "/master/tracker-protocols", label: "Protocolos", icon: Radio },
  { to: "/master/integrations", label: "Integrações", icon: Plug },
  { to: "/master/server", label: "Servidor", icon: Server },
] as const;

export function MasterShell({ children }: { children: ReactNode }) {
  const { admin, status, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (r) => r.location.pathname });
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  useEffect(() => {
    if (status === "expired") navigate({ to: "/master/login", replace: true });
  }, [status, navigate]);

  if (status !== "ok") {
    return (
      <GpsBootLoader />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <ShieldCheck className="h-4 w-4 text-sky-500" />
            Super Master
          </div>
          <nav className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              // SMS fica marcado também nas abas dele (Migração etc.).
              const active =
                pathname === item.to || (item.to === "/master/sms" && pathname !== "/master/vehicle-link" && SMS_TABS.some((t) => t.to === pathname));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <item.icon className="h-3.5 w-3.5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>{admin?.name}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-3.5 w-3.5" />
            Trocar senha
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void logout().then(() => navigate({ to: "/master/login" }))}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sair
          </Button>
        </div>
      </header>
      <main>{children}</main>
      <MasterChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
